/**
 * API Service for Lab Platform
 */

export const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

class ApiService {
  constructor() {
    this.baseURL = API_BASE;
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  getToken() {
    return this.token;
  }

  getFriendlyErrorMessage(status, errorData = {}) {
    // Extract the actual error message from the response
    let originalMessage = '';
    
    // Handle our backend error structure: {detail: {error: {code: "...", message: "..."}}}
    if (errorData.detail?.error?.message) {
      originalMessage = errorData.detail.error.message;
    } else if (errorData.detail?.message) {
      originalMessage = errorData.detail.message;
    } else if (typeof errorData.detail === 'string') {
      originalMessage = errorData.detail;
    } else if (Array.isArray(errorData.detail)) {
      // Handle validation errors
      originalMessage = errorData.detail.map(err => err.msg).join(', ');
    } else if (errorData.message) {
      originalMessage = errorData.message;
    }

    switch (status) {
      case 400:
        if (originalMessage.includes('Already enrolled')) {
          return 'You are already enrolled in this course. Check your dashboard to access it.';
        }
        if (originalMessage.includes('Invalid course')) {
          return 'Course not found. Please select a valid course.';
        }
        if (originalMessage.includes('Email already registered') || originalMessage.includes('DUPLICATE_EMAIL')) {
          return 'Email already registered';
        }
        if (originalMessage.includes('Mobile number already registered') || originalMessage.includes('DUPLICATE_MOBILE')) {
          return 'Mobile number already registered';
        }
        return originalMessage || 'Invalid request. Please check your input and try again.';
      
      case 401:
        return 'Session expired. Please log in again to continue.';
      
      case 403:
        return 'Access denied. You may need to upgrade your plan to access this content.';
      
      case 404:
        return 'Content not found. The requested resource may have been moved or removed.';
      
      case 500:
        return 'Server error. Our team has been notified. Please try again in a few minutes.';
      
      case 503:
        return 'Service temporarily unavailable. Please try again shortly.';
      
      default:
        return originalMessage || `Connection error (${status}). Please check your internet and try again.`;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Destructure headers out of options so spreading options doesn't override the merged headers
    const { headers: optionHeaders, ...restOptions } = options;

    const config = {
      ...restOptions,
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...optionHeaders,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const friendlyMessage = this.getFriendlyErrorMessage(response.status, errorData);
        throw new Error(friendlyMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // HTTP Method Helpers
  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  async post(endpoint, data = null, options = {}) {
    const config = {
      method: 'POST',
      ...options
    };
    
    if (data) {
      config.body = JSON.stringify(data);
    }
    
    // Return response in axios-like format for compatibility
    const responseData = await this.request(endpoint, config);
    return { data: responseData };
  }

  async put(endpoint, data = null, options = {}) {
    const config = {
      method: 'PUT',
      ...options
    };
    
    if (data) {
      config.body = JSON.stringify(data);
    }
    
    return this.request(endpoint, config);
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', ...options });
  }

  // Auth endpoints
  async register(userData) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async verifyToken() {
    return this.request('/api/auth/verify');
  }

  // Lab endpoints
  async getCourses() {
    return this.request('/api/courses');
  }

  async getCourseLabs(course) {
    return this.request(`/api/lab/list/${course}`);
  }

  async getLabDetail(course, labId) {
    return this.request(`/api/lab/${course}/${labId}`);
  }

  // Terminal management (Original)
  async startTerminal(course, labId) {
    return this.request('/api/terminal/start', {
      method: 'POST',
      body: JSON.stringify({ course, lab_id: labId }),
    });
  }

  async getTerminalStatus(course, labId) {
    return this.request(`/api/terminal/status?course=${course}&lab_id=${labId}&bump=true`);
  }

  async getActiveSession() {
    return this.request('/api/terminal/session');
  }

  async stopTerminal(course, labId) {
    return this.request('/api/terminal/stop', {
      method: 'POST',
      body: JSON.stringify({ course, lab_id: labId }),
    });
  }

  async sendHeartbeat(course, labId) {
    return this.request('/api/terminal/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ course, lab_id: labId }),
    });
  }

  // Enhanced Lab Management (Better Implementation)
  async startLab() {
    return this.request('/api/lab/start', {
      method: 'POST',
    });
  }

  async getLabStatus() {
    return this.request('/api/lab/status');
  }

  async stopLab() {
    return this.request('/api/lab/stop', {
      method: 'POST',
    });
  }

  // Verification endpoints
  async verifyTask(course, labId, taskId) {
    return this.request(`/api/verify/${course}/${labId}/${taskId}`, {
      method: 'POST',
    });
  }

  // Self-mark task complete (for cloud courses like Terraform)
  async markTaskComplete(course, labId, taskId) {
    return this.request(`/api/progress/mark-complete`, {
      method: 'POST',
      body: JSON.stringify({ course, lab_id: labId, task_id: taskId })
    });
  }

  // Progress endpoints
  async getMyProgress(course = null) {
    const params = course ? `?course=${course}` : '';
    return this.request(`/api/progress/my${params}`);
  }

  async getDashboardSummary() {
    return this.request('/api/progress/dashboard/summary');
  }

  async resetLabProgress(course, labId) {
    return this.request('/api/progress/reset', {
      method: 'POST',
      body: JSON.stringify({ course, lab_id: labId })
    });
  }

  // Payment API Methods (Razorpay Integration)
  async createOrder(orderData) {
    return this.request('/api/payments/create-order', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  }

  async verifyPayment(paymentData) {
    return this.request('/api/payments/verify', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async getMyPayments() {
    return this.request('/api/payments/my-payments');
  }

  async exportPayments() {
    return this.request('/api/payments/admin/export');
  }

  async checkCourseEnrollment(courseId) {
    return this.request(`/api/payments/enrollments/check/${courseId}`);
  }

  // User Pricing Info - KEY endpoint for "Existing Customer" discount model
  async getUserPricingInfo() {
    return this.request('/api/payments/user-pricing-info');
  }

  // All Access Pricing - Get personalized price based on existing courses
  async getAllAccessPricing() {
    return this.request('/api/payments/all-access-pricing');
  }

  // All Access Status - Check if user has All Access
  async getAllAccessStatus() {
    return this.request('/api/payments/user/all-access-status');
  }

  // Freemium Access Control APIs
  async getCourseAccess(courseId) {
    return this.request(`/api/payments/access/course/${courseId}`);
  }

  async checkLabAccess(courseId, labId) {
    return this.request(`/api/payments/access/lab/${courseId}/${labId}`);
  }

  // Lab Timer endpoints (new)
  async startLabTimer(course, labId) {
    return this.request('/api/terminal/timer/start', {
      method: 'POST',
      body: JSON.stringify({ course, lab_id: labId })
    });
  }

  async getLabTimerStatus(course, labId) {
    return this.request(`/api/terminal/timer/status?course=${course}&lab_id=${labId}`);
  }

  async completeLabTimer(course, labId) {
    return this.request('/api/terminal/timer/complete', {
      method: 'POST',
      body: JSON.stringify({ course, lab_id: labId })
    });
  }

  // Health check
  async healthCheck() {
    return this.request('/api/health');
  }

  // Mock Tests API
  async startMockTest(testData) {
    const response = await this.request('/api/mock-tests/start-test', {
      method: 'POST',
      body: JSON.stringify(testData)
    });
    return response;
  }

  async submitMockTest(testData) {
    const response = await this.request('/api/mock-tests/submit-test', {
      method: 'POST',
      body: JSON.stringify(testData)
    });
    return response;
  }

  async getMockTestStats() {
    const response = await this.request('/api/mock-tests/user-stats');
    return response;
  }

  async getMockTestAttempt(attemptId) {
    const response = await this.request(`/api/mock-tests/attempt/${attemptId}`);
    return response;
  }

  async resetMockTestProgress() {
    const response = await this.request('/api/mock-tests/reset-progress', {
      method: 'DELETE'
    });
    return response;
  }

  async getMockTestLeaderboard(limit = 10, category = 'ALL') {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (category && category !== 'ALL') {
      params.append('category', category);
    }
    const response = await this.request(`/api/mock-tests/leaderboard?${params.toString()}`);
    return response;
  }

  async getTestCatalog() {
    const response = await this.request('/api/mock-tests/test-catalog');
    return response;
  }

  async getTestQuestions(testId) {
    const response = await this.request(`/api/mock-tests/test-questions/${testId}`);
    return response;
  }

  async getTestHistory(testId) {
    const response = await this.request(`/api/mock-tests/test-history/${testId}`);
    return response;
  }

  // ============================================================
  // RHCSA Exam Practice APIs
  // ============================================================
  
  async getExamSets() {
    return this.request('/api/exam/sets');
  }

  async getExamDetail(examId) {
    return this.request(`/api/exam/detail/${examId}`);
  }

  async startExam(examId) {
    return this.request('/api/exam/start', {
      method: 'POST',
      body: JSON.stringify({ exam_id: examId })
    });
  }

  async getExamStatus() {
    return this.request('/api/exam/status');
  }

  async verifyExamTask(examId, taskId) {
    return this.request('/api/exam/verify', {
      method: 'POST',
      body: JSON.stringify({ exam_id: examId, task_id: taskId })
    });
  }

  async stopExam() {
    return this.request('/api/exam/stop', {
      method: 'POST'
    });
  }

  async getExamResults(examId) {
    return this.request(`/api/exam/results/${examId}`);
  }

  async sendExamHeartbeat() {
    return this.request('/api/exam/heartbeat', {
      method: 'POST'
    });
  }

  // ============================================================
  // DO188 Container Exam Practice APIs
  // ============================================================
  
  async getDO188ExamSets() {
    return this.request('/api/exam-do188/sets');
  }

  async getDO188ExamDetail(examId) {
    return this.request(`/api/exam-do188/detail/${examId}`);
  }

  async startDO188Exam(examId) {
    return this.request('/api/exam-do188/start', {
      method: 'POST',
      body: JSON.stringify({ exam_id: examId })
    });
  }

  async getDO188ExamStatus() {
    return this.request('/api/exam-do188/status');
  }

  async verifyDO188ExamTask(examId, taskId) {
    return this.request('/api/exam-do188/verify', {
      method: 'POST',
      body: JSON.stringify({ exam_id: examId, task_id: taskId })
    });
  }

  async stopDO188Exam() {
    return this.request('/api/exam-do188/stop', {
      method: 'POST'
    });
  }

  async getDO188ExamResults(examId) {
    return this.request(`/api/exam-do188/results/${examId}`);
  }

  async sendDO188ExamHeartbeat() {
    return this.request('/api/exam-do188/heartbeat', {
      method: 'POST'
    });
  }

  // ============================================================
  // EX294 Ansible Exam Practice APIs
  // ============================================================
  
  async getEX294ExamSets() {
    return this.request('/api/exam-ex294/sets');
  }

  async getEX294ExamDetail(examId) {
    return this.request(`/api/exam-ex294/detail/${examId}`);
  }

  async startEX294Exam(examId) {
    return this.request('/api/exam-ex294/start', {
      method: 'POST',
      body: JSON.stringify({ exam_id: examId })
    });
  }

  async getEX294ExamStatus() {
    return this.request('/api/exam-ex294/status');
  }

  async stopEX294Exam() {
    return this.request('/api/exam-ex294/stop', {
      method: 'POST'
    });
  }

  async getEX294ExamResults(examId) {
    return this.request(`/api/exam-ex294/results/${examId}`);
  }

  async sendEX294ExamHeartbeat() {
    return this.request('/api/exam-ex294/heartbeat', {
      method: 'POST'
    });
  }

  // ============= Notifications =============
  async getNotifications({ page = 1, limit = 20, filter = 'all' } = {}) {
    return this.request(`/api/notifications/my?page=${page}&limit=${limit}&filter=${filter}`);
  }

  async getNotificationsUnreadCount() {
    return this.request('/api/notifications/unread-count');
  }

  async markNotificationRead(id) {
    return this.request(`/api/notifications/${id}/read`, { method: 'POST' });
  }

  async markAllNotificationsRead() {
    return this.request('/api/notifications/mark-all-read', { method: 'POST' });
  }

  async dismissNotification(id) {
    return this.request(`/api/notifications/${id}/dismiss`, { method: 'POST' });
  }

  async getLabChangelog(courseId, labId) {
    return this.request(`/api/notifications/labs/${courseId}/${labId}/changelog`);
  }

  // --- User Profile ---
  async getUserProfile() {
    return this.request('/api/user/profile');
  }

  async updateUserProfile(data) {
    return this.request('/api/user/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateExtendedProfile(data) {
    return this.request('/api/user/profile/extended', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data) {
    return this.request('/api/user/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getUserStats() {
    return this.request('/api/user/stats');
  }

  async deleteCV() {
    return this.request('/api/user/cv', { method: 'DELETE' });
  }

  async getCertificateCatalog() {
    return this.request('/api/user/certificates/catalog');
  }

  async getUserCertificates() {
    return this.request('/api/user/certificates');
  }

  async removeUserCertificate(certId) {
    return this.request(`/api/user/certificates/${certId}`, { method: 'DELETE' });
  }

  // --- Jobs (public) ---
  async getJobs() {
    return this.request('/api/jobs');
  }

  // --- Admin: Jobs ---
  async adminGetJobs(adminToken) {
    return this.request('/api/admin/jobs', {
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminCreateJob(data, adminToken) {
    return this.request('/api/admin/jobs', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminUpdateJob(jobId, data, adminToken) {
    return this.request(`/api/admin/jobs/${jobId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminToggleJob(jobId, adminToken) {
    return this.request(`/api/admin/jobs/${jobId}/toggle`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminDeleteJob(jobId, adminToken) {
    return this.request(`/api/admin/jobs/${jobId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminBlastJob(jobId, adminToken) {
    return this.request(`/api/admin/jobs/${jobId}/blast`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  // --- Admin: Certificate Catalog ---
  async adminGetCertCatalog(adminToken) {
    return this.request('/api/admin/certificates/catalog', {
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminCreateCert(data, adminToken) {
    return this.request('/api/admin/certificates/catalog', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminUpdateCert(certId, data, adminToken) {
    return this.request(`/api/admin/certificates/catalog/${certId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminDeleteCert(certId, adminToken) {
    return this.request(`/api/admin/certificates/catalog/${certId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }

  async adminGetUserProfile(userId, adminToken) {
    return this.request(`/api/admin/users/${userId}/profile`, {
      headers: { Authorization: `Bearer ${adminToken}` },
    });
  }
}

export const apiService = new ApiService();
export default apiService;